import { h } from 'vue';
import { createRouter, createWebHistory, RouteRecordRaw, RouterView } from "vue-router";
import store from "@/store";
import { Mutations, Actions } from "@/store/enums/StoreEnums";
import JwtService from "@/core/services/JwtService";
import ApiService from "@/core/services/ApiService";
import AuthService from "@/core/services/AuthService";
import { tricklingProgress } from '@/core/plugins/trickling-progress';
import { REPORT_REGISTRY } from '@/core/config/reports/ReportRegistry';

const routes: Array<RouteRecordRaw> = [
    // routes using main layout with authentication
    {
        path: "/",
        redirect: "/dashboard",
        component: () => import("@/layouts/main-layout/MainLayout.vue"),
        meta: {
            middleware: "auth",
        },
        children: [
            {
                path: "/dashboard",
                name: "dashboard",
                component: () => import("@/views/pages/dashboard/Dashboard-v2.vue"),
                meta: {
                    pageTitle: "Dashboard",
                    breadcrumbs: ["Dashboards"],
                    userFeature: null,
                },
            },
            // Front Office
            {
                path: "/front-office/booking-chart",
                name: "booking-chart",
                component: () => import("@/views/pages/front-office/booking/chart/BookingChartPage.vue"),
                meta: {
                    pageTitle: "Booking Chart",
                    breadcrumbs: ["Front Office", "Reservations", "Booking Chart"],
                    userFeature: { featureName: 'booking_chart', metaName: 'can_view' },
                },
            },
            {
                path: "/front-office/reservation-list",
                component: { render: () => h(RouterView) },
                children: [
                    {
                        path: '',
                        name: "reservation-list",
                        component: () => import("@/views/pages/front-office/reservation-list/ReservationList.vue"),
                        meta: {
                            pageTitle: "Reservation List",
                            breadcrumbs: ["Front Office", "Reservations", "Reservation List"],
                            userFeature: { featureName: 'reservation_list', metaName: 'can_view' },
                        },
                    },
                    {
                        path: 'create',
                        name: "reservation-list-create",
                        component: () => import("@/views/pages/front-office/booking/Create.vue"),
                        meta: {
                            pageTitle: "Create New Booking",
                            breadcrumbs: ["Front Office", "Reservations", "Reservation List", "Create New Booking"],
                            userFeature: { featureName: 'reservation_list', metaName: 'can_add', fallbackFeatureName: 'booking_editor' },
                        },
                    },
                    {
                        path: 'edit/:id',
                        name: "reservation-list-edit",
                        component: () => import("@/views/pages/front-office/booking/Edit.vue"),
                        meta: {
                            pageTitle: "Edit Booking",
                            breadcrumbs: ["Front Office", "Reservations", "Reservation List", "Edit Booking"],
                            userFeature: { featureName: 'reservation_list', metaName: 'can_edit', fallbackFeatureName: 'booking_editor' },
                        },
                    },
                    {
                        // Full booking folio, opened from Booking Chart and Reservation List.
                        path: 'booking/:bookingRoomId',
                        name: "booking-detail",
                        component: () => import("@/views/pages/front-office/booking/detail-info/BookingDetailPage.vue"),
                        meta: {
                            pageTitle: "Booking Detail",
                            breadcrumbs: ["Front Office", "Reservations", "Booking Detail"],
                            userFeature: { featureName: 'reservation_list', metaName: 'can_view', fallbackFeatureName: 'booking_editor' },
                        },
                    },
                ],
            },
            {
                path: "/front-office/booking",
                component: { render: () => h(RouterView) },
                children: [
                    {
                        path: '',
                        name: "booking",
                        component: () => import("@/views/pages/front-office/booking/Booking.vue"),
                        meta: {
                            pageTitle: "Bookings",
                            breadcrumbs: ["Front Office", "Bookings"],
                            userFeature: { featureName: 'bookings', metaName: 'can_view' },
                        },
                    },
                    {
                        path: 'create',
                        name: "booking-create",
                        component: () => import("@/views/pages/front-office/booking/Create.vue"),
                        meta: {
                            pageTitle: "Create New Booking",
                            breadcrumbs: ["Front Office", "Booking", "Create New Booking"],
                            userFeature: { featureName: 'booking_editor', metaName: 'can_add', fallbackFeatureName: 'reservation_list' },
                        },
                    },
                    {
                        path: 'edit/:id',
                        name: "booking-edit",
                        component: () => import("@/views/pages/front-office/booking/Edit.vue"),
                        meta: {
                            pageTitle: "Edit Booking",
                            breadcrumbs: ["Front Office", "Booking", "Edit Booking"],
                            userFeature: { featureName: 'booking_editor', metaName: 'can_edit', fallbackFeatureName: 'reservation_list' },
                        },
                    },
                ],
            },
            {
                path: "/front-office/unpaid-bookings",
                name: "booking-outstanding",
                component: () => import("@/views/pages/front-office/booking-outstanding/BookingOutstanding.vue"),
                meta: {
                    pageTitle: "Outstanding Balance",
                    breadcrumbs: ["Front Office", "Cashiering", "Outstanding Balance"],
                    userFeature: { featureName: 'unpaid_bookings', metaName: 'can_view' },
                },
            },
            {
                path: "/front-office/tentative-reservations",
                name: "booking-on-hold",
                component: () => import("@/views/pages/front-office/booking-onhold/BookingOnHold.vue"),
                meta: {
                    pageTitle: "Tentative Reservations",
                    breadcrumbs: ["Front Office", "Tentative Reservations"],
                    userFeature: { featureName: 'tentative_reservations', metaName: 'can_view' },
                },
            },
            {
                path: "/front-office/guest-profile",
                name: "guest-profile",
                component: () => import("@/views/pages/front-office/guest-profile/GuestProfile.vue"),
                meta: {
                    pageTitle: "Guest Profile",
                    breadcrumbs: ["Front Office", "Guests", "Guest Profile"],
                    userFeature: { featureName: 'guest_profile', metaName: 'can_view' },
                },
            },
            {
                path: "/front-office/receive-payment",
                name: "payment-receive",
                component: () => import("@/views/pages/front-office/payment-receive/PaymentReceive.vue"),
                meta: {
                    pageTitle: "Receive Payment",
                    breadcrumbs: ["Front Office", "Cashiering", "Receive Payment"],
                    userFeature: { featureName: 'receive_payment', metaName: 'can_view' },
                },
            },
            {
                path: "/front-office/night-audit",
                name: "night-audit",
                component: () => import("@/views/pages/front-office/night-audit/NightAudit.vue"),
                meta: {
                    pageTitle: "Night Audit",
                    breadcrumbs: ["Front Office", "Night Audit"],
                    userFeature: { featureName: 'night_audit_day_close', metaName: 'can_view' },
                },
            },
            {
                path: "/front-office/daily-guests",
                name: "guest-list-daily",
                component: () => import("@/views/pages/front-office/guest-list-daily/GuestListDaily.vue"),
                meta: {
                    pageTitle: "Daily Guests",
                    breadcrumbs: ["Front Office", "Front Desk", "Daily Guests"],
                    userFeature: { featureName: 'daily_guests', metaName: 'can_view' },
                },
            },
            {
                path: "/front-office/arrivals",
                name: "guest-arrivals",
                component: () => import("@/views/pages/front-office/guest-list-daily/GuestListDaily.vue"),
                meta: {
                    pageTitle: "Arrivals",
                    breadcrumbs: ["Front Office", "Front Desk", "Arrivals"],
                    listType: "arival",
                    userFeature: { featureName: 'daily_guests', metaName: 'can_view' },
                },
            },
            {
                path: "/front-office/in-house-guests",
                name: "guest-in-house",
                component: () => import("@/views/pages/front-office/guest-list-daily/GuestListDaily.vue"),
                meta: {
                    pageTitle: "In-House Guests",
                    breadcrumbs: ["Front Office", "Front Desk", "In-House Guests"],
                    listType: "in_house",
                    userFeature: { featureName: 'daily_guests', metaName: 'can_view' },
                },
            },
            {
                path: "/front-office/departures",
                name: "guest-departures",
                component: () => import("@/views/pages/front-office/guest-list-daily/GuestListDaily.vue"),
                meta: {
                    pageTitle: "Departures",
                    breadcrumbs: ["Front Office", "Front Desk", "Departures"],
                    listType: "depature",
                    userFeature: { featureName: 'daily_guests', metaName: 'can_view' },
                },
            },
            // House Keeping
            {
                path: "/house-keeping/board",
                name: "housekeeping-board",
                component: () => import("@/views/pages/house-keeping/HousekeepingBoard.vue"),
                meta: {
                    pageTitle: "Housekeeping Board",
                    breadcrumbs: ["House Keeping", "Housekeeping Board"],
                    userFeature: { featureName: 'housekeeping_board', metaName: 'can_view' },
                },
            },
            {
                path: "/house-keeping/audit-log",
                name: "housekeeping-audit-log",
                component: () => import("@/views/pages/house-keeping/HousekeepingAuditLog.vue"),
                meta: {
                    pageTitle: "Housekeeping Audit Log",
                    breadcrumbs: ["House Keeping", "Audit Log"],
                    userFeature: { featureName: 'housekeeping_audit_log', metaName: 'can_view' },
                },
            },
            // Back Office -> Finance & Accounting
            {
                path: "/finance-accounting/chart-of-account",
                name: "chart-of-account",
                component: () => import("@/views/pages/back-office/chart-of-account/ChartofAccount.vue"),
                meta: {
                    pageTitle: "Chart of Account",
                    breadcrumbs: ["Finance", "Chart of Account"],
                    userFeature: { featureName: 'chart_of_account', metaName: 'can_view' },
                },
            },
            {
                path: "/finance-accounting/chart-of-account/:coaId/transactions",
                name: "chart-of-account-transactions",
                component: () => import("@/views/pages/back-office/chart-of-account/ChartOfAccountTransactions.vue"),
                meta: {
                    pageTitle: "COA Transaction Detail",
                    breadcrumbs: ["Finance", "Chart of Account", "Transactions"],
                    userFeature: { featureName: 'chart_of_account', metaName: 'can_view' },
                },
            },
            {
                path: "/finance-accounting/general-journal",
                component: { render: () => h(RouterView) },
                children: [
                    {
                        path: "",
                        name: "general-journal",
                        component: () => import("@/views/pages/back-office/general-journal/GeneralJournalList.vue"),
                        meta: {
                            pageTitle: "General Journal",
                            breadcrumbs: ["Finance", "General Journal"],
                            userFeature: { featureName: 'general_journal', metaName: 'can_view' },
                        },
                    },
                    {
                        path: "new",
                        name: "general-journal-new",
                        component: () => import("@/views/pages/back-office/general-journal/GeneralJournalForm.vue"),
                        meta: {
                            pageTitle: "New General Journal",
                            breadcrumbs: ["Finance", "General Journal", "New"],
                            userFeature: { featureName: 'general_journal', metaName: 'can_add' },
                        },
                    },
                    {
                        path: ":id",
                        name: "general-journal-detail",
                        component: () => import("@/views/pages/back-office/general-journal/GeneralJournalForm.vue"),
                        meta: {
                            pageTitle: "General Journal",
                            breadcrumbs: ["Finance", "General Journal", "Detail"],
                            userFeature: { featureName: 'general_journal', metaName: 'can_view' },
                        },
                    },
                ],
            },
            {
                path: "/finance-accounting/finance-cutoff",
                name: "finance-cutoff",
                component: () => import("@/views/pages/back-office/finance-cutoff/FinanceCutoff.vue"),
                meta: {
                    pageTitle: "Finance Cutoff",
                    breadcrumbs: ["Finance", "Finance Cutoff"],
                    userFeature: { featureName: 'finance_cutoff', metaName: 'can_view' },
                },
            },
            {
                path: "/finance-accounting/ota-ledger",
                component: { render: () => h(RouterView) },
                children: [
                    {
                        path: "",
                        name: "ota-ledger",
                        component: () => import("@/views/pages/back-office/ota-ledger/OtaLedgerList.vue"),
                        meta: {
                            pageTitle: "OTA Ledger",
                            breadcrumbs: ["Finance", "OTA Ledger"],
                            userFeature: { featureName: 'ota_ledger', metaName: 'can_view' },
                        },
                    },
                    {
                        path: ":agentId/reconcile",
                        name: "ota-ledger-reconcile",
                        component: () => import("@/views/pages/back-office/ota-ledger/OtaLedgerReconcile.vue"),
                        meta: {
                            pageTitle: "Reconcile OTA Ledger",
                            breadcrumbs: ["Finance", "OTA Ledger", "Reconcile"],
                            userFeature: { featureName: 'ota_ledger', metaName: 'can_add' },
                        },
                    },
                    {
                        path: ":agentId",
                        name: "ota-ledger-detail",
                        component: () => import("@/views/pages/back-office/ota-ledger/OtaLedgerDetail.vue"),
                        meta: {
                            pageTitle: "OTA Ledger",
                            breadcrumbs: ["Finance", "OTA Ledger", "Detail"],
                            userFeature: { featureName: 'ota_ledger', metaName: 'can_view' },
                        },
                    },
                ],
            },
            {
                path: "/finance-accounting/taxes-and-services",
                name: "tax-and-service-list",
                component: () => import("@/views/pages/back-office/tax-and-service-list/TaxnServiceList.vue"),
                meta: {
                    pageTitle: "Taxes And Services",
                    breadcrumbs: ["Hotel Setup", "Taxes And Services"],
                    userFeature: { featureName: 'taxes_services', metaName: 'can_view' },
                },
            },
            {
                path: "/finance-accounting/sources-agents",
                name: "agent-property-list",
                component: () => import("@/views/pages/back-office/agent-property-list/AgentPropertyList.vue"),
                meta: {
                    pageTitle: "Sources and Agents",
                    breadcrumbs: ["Hotel Setup", "Sources and Agents"],
                    userFeature: { featureName: 'source_and_agents', metaName: 'can_view' },
                },
            },
            {
                path: "/finance-accounting/financial-reports",
                component: { render: () => h(RouterView) },
                children: [
                    {
                        path: "",
                        name: "fa-financial-reports",
                        component: () => import("@/views/pages/back-office/financial-reports/FinancialReportsHub.vue"),
                        meta: {
                            pageTitle: "Financial Reports",
                            breadcrumbs: ["Finance", "Financial Reports"],
                            userFeature: { featureName: 'financial_reports', metaName: 'can_view' },
                        },
                    },
                    {
                        path: "balance-sheet",
                        name: "fa-balance-sheet",
                        component: () => import("@/views/pages/back-office/financial-reports/BalanceSheetReport.vue"),
                        meta: {
                            pageTitle: "Balance Sheet",
                            breadcrumbs: ["Finance", "Financial Reports", "Balance Sheet"],
                            userFeature: { featureName: 'financial_reports', metaName: 'can_view' },
                        },
                    },
                    {
                        path: "profit-loss",
                        name: "fa-profit-loss",
                        component: () => import("@/views/pages/back-office/financial-reports/ProfitLossReport.vue"),
                        meta: {
                            pageTitle: "Profit & Loss",
                            breadcrumbs: ["Finance", "Financial Reports", "Profit & Loss"],
                            userFeature: { featureName: 'financial_reports', metaName: 'can_view' },
                        },
                    },
                    {
                        path: "trial-balance",
                        name: "fa-trial-balance",
                        component: () => import("@/views/pages/back-office/financial-reports/TrialBalanceReport.vue"),
                        meta: {
                            pageTitle: "Trial Balance",
                            breadcrumbs: ["Finance", "Financial Reports", "Trial Balance"],
                            userFeature: { featureName: 'financial_reports', metaName: 'can_view' },
                        },
                    },
                    {
                        path: "general-ledger",
                        name: "fa-general-ledger",
                        component: () => import("@/views/pages/back-office/financial-reports/GeneralLedgerReport.vue"),
                        meta: {
                            pageTitle: "General Ledger",
                            breadcrumbs: ["Finance", "Financial Reports", "General Ledger"],
                            userFeature: { featureName: 'financial_reports', metaName: 'can_view' },
                        },
                    },
                    {
                        path: "cash-flow",
                        name: "fa-cash-flow",
                        component: () => import("@/views/pages/back-office/financial-reports/CashFlowReport.vue"),
                        meta: {
                            pageTitle: "Cash Flow Statement",
                            breadcrumbs: ["Finance", "Financial Reports", "Cash Flow Statement"],
                            userFeature: { featureName: 'financial_reports', metaName: 'can_view' },
                        },
                    },
                    {
                        path: "ar-aging",
                        name: "fa-ar-aging",
                        component: () => import("@/views/pages/back-office/financial-reports/ArAgingReport.vue"),
                        meta: {
                            pageTitle: "AR Aging",
                            breadcrumbs: ["Finance", "Financial Reports", "AR Aging"],
                            userFeature: { featureName: 'financial_reports', metaName: 'can_view' },
                        },
                    },
                    {
                        path: "ap-aging",
                        name: "fa-ap-aging",
                        component: () => import("@/views/pages/back-office/financial-reports/ApAgingReport.vue"),
                        meta: {
                            pageTitle: "AP Aging",
                            breadcrumbs: ["Finance", "Financial Reports", "AP Aging"],
                            userFeature: { featureName: 'financial_reports', metaName: 'can_view' },
                        },
                    },
                    {
                        path: "night-audit-flash",
                        name: "fa-night-audit-flash",
                        component: () => import("@/views/pages/back-office/financial-reports/NightAuditFlashReport.vue"),
                        meta: {
                            pageTitle: "Night Audit Flash",
                            breadcrumbs: ["Finance", "Financial Reports", "Night Audit Flash"],
                            userFeature: { featureName: 'financial_reports', metaName: 'can_view' },
                        },
                    },
                    {
                        path: "tax-summary",
                        name: "fa-tax-summary",
                        component: () => import("@/views/pages/back-office/financial-reports/TaxServiceSummaryReport.vue"),
                        meta: {
                            pageTitle: "Tax & Service Summary",
                            breadcrumbs: ["Finance", "Financial Reports", "Tax & Service Summary"],
                            userFeature: { featureName: 'financial_reports', metaName: 'can_view' },
                        },
                    },
                    {
                        path: "journal-entries",
                        name: "fa-journal-entries",
                        component: () => import("@/views/pages/back-office/financial-reports/JournalEntriesLogReport.vue"),
                        meta: {
                            pageTitle: "Journal Entries Log",
                            breadcrumbs: ["Finance", "Financial Reports", "Journal Entries Log"],
                            userFeature: { featureName: 'financial_reports', metaName: 'can_view' },
                        },
                    },
                    {
                        path: "revenue-summary",
                        name: "fa-revenue-summary",
                        component: () => import("@/views/pages/back-office/financial-reports/RevenueSummaryReport.vue"),
                        meta: {
                            pageTitle: "Revenue Summary",
                            breadcrumbs: ["Finance", "Financial Reports", "Revenue Summary"],
                            userFeature: { featureName: 'financial_reports', metaName: 'can_view' },
                        },
                    },
                    {
                        path: "ota-settlement",
                        name: "fa-ota-settlement",
                        component: () => import("@/views/pages/back-office/financial-reports/OtaSettlementPackReport.vue"),
                        meta: {
                            pageTitle: "OTA Settlement Pack",
                            breadcrumbs: ["Finance", "Financial Reports", "OTA Settlement Pack"],
                            userFeature: { featureName: 'financial_reports', metaName: 'can_view' },
                        },
                    },
                ],
            },
            {
                path: "/finance-accounting/contacts",
                component: { render: () => h(RouterView) },
                children: [
                    {
                        path: "",
                        name: "contacts",
                        component: () => import("@/views/pages/back-office/contacts/ContactList.vue"),
                        meta: {
                            pageTitle: "Contacts",
                            breadcrumbs: ["Finance", "Contacts"],
                            userFeature: {
                                featureName: 'contacts',
                                metaName: 'can_view',
                                fallbackFeatureName: 'account_payable_supplier_list',
                            },
                        },
                    },
                    {
                        path: "new",
                        name: "contacts-new",
                        component: () => import("@/views/pages/back-office/contacts/ContactForm.vue"),
                        meta: {
                            pageTitle: "New Contact",
                            breadcrumbs: ["Finance", "Contacts", "New"],
                            userFeature: { featureName: 'contacts', metaName: 'can_add' },
                        },
                    },
                    {
                        path: ":id",
                        name: "contacts-detail",
                        component: () => import("@/views/pages/back-office/contacts/ContactForm.vue"),
                        meta: {
                            pageTitle: "Edit Contact",
                            breadcrumbs: ["Finance", "Contacts", "Edit"],
                            userFeature: { featureName: 'contacts', metaName: 'can_view' },
                        },
                    },
                ],
            },
            {
                path: "/finance-accounting/expenses",
                component: { render: () => h(RouterView) },
                children: [
                    {
                        path: "",
                        name: "ap-expenses",
                        component: () => import("@/views/pages/back-office/expenses/ExpenseList.vue"),
                        meta: {
                            pageTitle: "Expenses",
                            breadcrumbs: ["Finance", "Expenses"],
                            userFeature: { featureName: 'expenses', metaName: 'can_view' },
                        },
                    },
                    {
                        path: "new",
                        name: "ap-expenses-create",
                        component: () => import("@/views/pages/back-office/expenses/ExpenseForm.vue"),
                        meta: {
                            pageTitle: "Create Expense",
                            breadcrumbs: ["Finance", "Expenses", "Create"],
                            userFeature: { featureName: 'expenses', metaName: 'can_add' },
                        },
                    },
                    {
                        path: ":id",
                        name: "ap-expenses-edit",
                        component: () => import("@/views/pages/back-office/expenses/ExpenseForm.vue"),
                        meta: {
                            pageTitle: "Edit Expense",
                            breadcrumbs: ["Finance", "Expenses", "Edit"],
                            userFeature: { featureName: 'expenses', metaName: 'can_view' },
                        },
                    },
                ],
            },
            {
                path: "/finance-accounting/cash-bank",
                component: { render: () => h(RouterView) },
                children: [
                    {
                        path: "",
                        name: "fa-cash-bank",
                        component: () => import("@/views/pages/back-office/cash-bank/CashBankList.vue"),
                        meta: {
                            pageTitle: "Cash & Bank",
                            breadcrumbs: ["Finance", "Cash & Bank"],
                            userFeature: { featureName: 'cash_bank', metaName: 'can_view' },
                        },
                    },
                    {
                        path: "accounts/new",
                        name: "fa-cash-bank-account-create",
                        component: () => import("@/views/pages/back-office/cash-bank/CashBankAccountForm.vue"),
                        meta: {
                            pageTitle: "Create Cash Account",
                            breadcrumbs: ["Finance", "Cash & Bank", "Create account"],
                            userFeature: { featureName: 'cash_bank', metaName: 'can_add' },
                        },
                    },
                    {
                        path: "transfer/new",
                        name: "fa-cash-bank-transfer-create",
                        component: () => import("@/views/pages/back-office/cash-bank/CashBankTransferForm.vue"),
                        meta: {
                            pageTitle: "Transfer Funds",
                            breadcrumbs: ["Finance", "Cash & Bank", "Transfer"],
                            userFeature: { featureName: 'cash_bank', metaName: 'can_add' },
                        },
                    },
                    {
                        path: "transfer/:id",
                        name: "fa-cash-bank-transfer-edit",
                        component: () => import("@/views/pages/back-office/cash-bank/CashBankTransferForm.vue"),
                        meta: {
                            pageTitle: "Transfer Funds",
                            breadcrumbs: ["Finance", "Cash & Bank", "Transfer"],
                            userFeature: { featureName: 'cash_bank', metaName: 'can_view' },
                        },
                    },
                    {
                        path: "receive/new",
                        name: "fa-cash-bank-receive-create",
                        component: () => import("@/views/pages/back-office/cash-bank/CashBankMoneyForm.vue"),
                        meta: {
                            pageTitle: "Receive Money",
                            breadcrumbs: ["Finance", "Cash & Bank", "Receive money"],
                            userFeature: { featureName: 'cash_bank', metaName: 'can_add' },
                        },
                    },
                    {
                        path: "receive/:id",
                        name: "fa-cash-bank-receive-edit",
                        component: () => import("@/views/pages/back-office/cash-bank/CashBankMoneyForm.vue"),
                        meta: {
                            pageTitle: "Receive Money",
                            breadcrumbs: ["Finance", "Cash & Bank", "Receive money"],
                            userFeature: { featureName: 'cash_bank', metaName: 'can_view' },
                        },
                    },
                    {
                        path: "pay/new",
                        name: "fa-cash-bank-pay-create",
                        component: () => import("@/views/pages/back-office/cash-bank/CashBankMoneyForm.vue"),
                        meta: {
                            pageTitle: "Pay Money",
                            breadcrumbs: ["Finance", "Cash & Bank", "Pay money"],
                            userFeature: { featureName: 'cash_bank', metaName: 'can_add' },
                        },
                    },
                    {
                        path: "pay/:id",
                        name: "fa-cash-bank-pay-edit",
                        component: () => import("@/views/pages/back-office/cash-bank/CashBankMoneyForm.vue"),
                        meta: {
                            pageTitle: "Pay Money",
                            breadcrumbs: ["Finance", "Cash & Bank", "Pay money"],
                            userFeature: { featureName: 'cash_bank', metaName: 'can_view' },
                        },
                    },
                ],
            },
            {
                path: "/finance-accounting/sales",
                component: { render: () => h(RouterView) },
                children: [
                    {
                        path: "",
                        name: "fa-sales",
                        component: () => import("@/views/pages/back-office/sales/SalesInvoiceList.vue"),
                        meta: {
                            pageTitle: "Sales",
                            breadcrumbs: ["Finance", "Sales"],
                            userFeature: { featureName: 'sales', metaName: 'can_view' },
                        },
                    },
                    {
                        path: "new",
                        name: "fa-sales-create",
                        component: () => import("@/views/pages/back-office/sales/SalesInvoiceForm.vue"),
                        meta: {
                            pageTitle: "Create Sales",
                            breadcrumbs: ["Finance", "Sales", "Create"],
                            userFeature: { featureName: 'sales', metaName: 'can_add' },
                        },
                    },
                    {
                        path: ":id",
                        name: "fa-sales-edit",
                        component: () => import("@/views/pages/back-office/sales/SalesInvoiceForm.vue"),
                        meta: {
                            pageTitle: "Edit Sales",
                            breadcrumbs: ["Finance", "Sales", "Edit"],
                            userFeature: { featureName: 'sales', metaName: 'can_view' },
                        },
                    },
                ],
            },
            {
                path: "/finance-accounting/purchases",
                component: { render: () => h(RouterView) },
                children: [
                    {
                        path: "",
                        redirect: { name: "fa-purchase-invoices" },
                    },
                    {
                        path: "invoices",
                        name: "fa-purchase-invoices",
                        component: () => import("@/views/pages/back-office/purchases/PurchaseList.vue"),
                        meta: {
                            pageTitle: "Purchase Invoice",
                            breadcrumbs: ["Finance", "Purchases", "Invoice"],
                            userFeature: { featureName: 'purchases', metaName: 'can_view' },
                            purchaseDocumentType: 'purchase_invoice',
                        },
                    },
                    {
                        path: "invoices/new",
                        name: "fa-purchase-invoices-create",
                        component: () => import("@/views/pages/back-office/purchases/PurchaseForm.vue"),
                        meta: {
                            pageTitle: "Create Purchase Invoice",
                            breadcrumbs: ["Finance", "Purchases", "Invoice", "Create"],
                            userFeature: { featureName: 'purchases', metaName: 'can_add' },
                            purchaseDocumentType: 'purchase_invoice',
                        },
                    },
                    {
                        path: "invoices/:id",
                        name: "fa-purchase-invoices-edit",
                        component: () => import("@/views/pages/back-office/purchases/PurchaseForm.vue"),
                        meta: {
                            pageTitle: "Edit Purchase Invoice",
                            breadcrumbs: ["Finance", "Purchases", "Invoice", "Edit"],
                            userFeature: { featureName: 'purchases', metaName: 'can_view' },
                            purchaseDocumentType: 'purchase_invoice',
                        },
                    },
                    {
                        path: "orders",
                        name: "fa-purchase-orders",
                        component: () => import("@/views/pages/back-office/purchases/PurchaseList.vue"),
                        meta: {
                            pageTitle: "Purchase Order",
                            breadcrumbs: ["Finance", "Purchases", "Order"],
                            userFeature: { featureName: 'purchases', metaName: 'can_view' },
                            purchaseDocumentType: 'purchase_order',
                        },
                    },
                    {
                        path: "orders/new",
                        name: "fa-purchase-orders-create",
                        component: () => import("@/views/pages/back-office/purchases/PurchaseForm.vue"),
                        meta: {
                            pageTitle: "Create Purchase Order",
                            breadcrumbs: ["Finance", "Purchases", "Order", "Create"],
                            userFeature: { featureName: 'purchases', metaName: 'can_add' },
                            purchaseDocumentType: 'purchase_order',
                        },
                    },
                    {
                        path: "orders/:id",
                        name: "fa-purchase-orders-edit",
                        component: () => import("@/views/pages/back-office/purchases/PurchaseForm.vue"),
                        meta: {
                            pageTitle: "Edit Purchase Order",
                            breadcrumbs: ["Finance", "Purchases", "Order", "Edit"],
                            userFeature: { featureName: 'purchases', metaName: 'can_view' },
                            purchaseDocumentType: 'purchase_order',
                        },
                    },
                    {
                        path: "quotes",
                        name: "fa-purchase-quotes",
                        component: () => import("@/views/pages/back-office/purchases/PurchaseList.vue"),
                        meta: {
                            pageTitle: "Purchase Quote",
                            breadcrumbs: ["Finance", "Purchases", "Quote"],
                            userFeature: { featureName: 'purchases', metaName: 'can_view' },
                            purchaseDocumentType: 'purchase_quote',
                        },
                    },
                    {
                        path: "quotes/new",
                        name: "fa-purchase-quotes-create",
                        component: () => import("@/views/pages/back-office/purchases/PurchaseForm.vue"),
                        meta: {
                            pageTitle: "Create Purchase Quote",
                            breadcrumbs: ["Finance", "Purchases", "Quote", "Create"],
                            userFeature: { featureName: 'purchases', metaName: 'can_add' },
                            purchaseDocumentType: 'purchase_quote',
                        },
                    },
                    {
                        path: "quotes/:id",
                        name: "fa-purchase-quotes-edit",
                        component: () => import("@/views/pages/back-office/purchases/PurchaseForm.vue"),
                        meta: {
                            pageTitle: "Edit Purchase Quote",
                            breadcrumbs: ["Finance", "Purchases", "Quote", "Edit"],
                            userFeature: { featureName: 'purchases', metaName: 'can_view' },
                            purchaseDocumentType: 'purchase_quote',
                        },
                    },
                    {
                        path: "requests",
                        name: "fa-purchase-requests",
                        component: () => import("@/views/pages/back-office/purchases/PurchaseList.vue"),
                        meta: {
                            pageTitle: "Purchase Request",
                            breadcrumbs: ["Finance", "Purchases", "Request"],
                            userFeature: { featureName: 'purchases', metaName: 'can_view' },
                            purchaseDocumentType: 'purchase_request',
                        },
                    },
                    {
                        path: "requests/new",
                        name: "fa-purchase-requests-create",
                        component: () => import("@/views/pages/back-office/purchases/PurchaseForm.vue"),
                        meta: {
                            pageTitle: "Create Purchase Request",
                            breadcrumbs: ["Finance", "Purchases", "Request", "Create"],
                            userFeature: { featureName: 'purchases', metaName: 'can_add' },
                            purchaseDocumentType: 'purchase_request',
                        },
                    },
                    {
                        path: "requests/:id",
                        name: "fa-purchase-requests-edit",
                        component: () => import("@/views/pages/back-office/purchases/PurchaseForm.vue"),
                        meta: {
                            pageTitle: "Edit Purchase Request",
                            breadcrumbs: ["Finance", "Purchases", "Request", "Edit"],
                            userFeature: { featureName: 'purchases', metaName: 'can_view' },
                            purchaseDocumentType: 'purchase_request',
                        },
                    },
                ],
            },
            {
                path: "/finance-accounting/payment-options",
                component: { render: () => h(RouterView) },
                children: [
                    {
                        path: '',
                        name: "payment-options-list",
                        component: () => import("@/views/pages/back-office/payment-options-list/PaymentOptionsList.vue"),
                        meta: {
                            pageTitle: "Payment Options",
                            breadcrumbs: ["Hotel Setup", "Payment Options"],
                            userFeature: { featureName: 'payment_methods', metaName: 'can_view' },
                        },
                    },
                    {
                        path: 'payment-methods/:id',
                        name: 'payment-options-list-payment-methods',
                        component: () => import("@/views/pages/back-office/payment-options-list/PaymentMethodList.vue"),
                        meta: {
                            pageTitle: "Payment Methods",
                            breadcrumbs: ["Finance", "Payment Options", "Payment Methods"],
                            userFeature: { featureName: 'payment_methods', metaName: 'can_view' },
                        },
                    },
                ]
            },
            {
                path: "/finance-accounting/discount-options",
                name: "discount-options-list",
                component: () => import("@/views/pages/back-office/discount-options-list/DiscountOptionsList.vue"),
                meta: {
                    pageTitle: "Discount Options",
                    breadcrumbs: ["Hotel Setup", "Discount Options"],
                    userFeature: { featureName: 'discount_options', metaName: 'can_view' },
                },
            },
            {
                path: "/finance-accounting/products",
                component: { render: () => h(RouterView) },
                children: [
                    {
                        path: "",
                        name: "products",
                        component: () => import("@/views/pages/back-office/products/ProductList.vue"),
                        meta: {
                            pageTitle: "Products",
                            breadcrumbs: ["Hotel Setup", "Products"],
                            userFeature: { featureName: 'products', metaName: 'can_view' },
                        },
                    },
                    {
                        path: "new",
                        name: "products-create",
                        component: () => import("@/views/pages/back-office/products/ProductForm.vue"),
                        meta: {
                            pageTitle: "Create Product",
                            breadcrumbs: ["Finance", "Products", "Create"],
                            userFeature: { featureName: 'products', metaName: 'can_add' },
                        },
                    },
                    {
                        path: ":id",
                        name: "products-edit",
                        component: () => import("@/views/pages/back-office/products/ProductForm.vue"),
                        meta: {
                            pageTitle: "Edit Product",
                            breadcrumbs: ["Finance", "Products", "Edit"],
                            userFeature: { featureName: 'products', metaName: 'can_view' },
                        },
                    },
                ],
            },
            {
                path: "/finance-accounting/product-category",
                name: "product-category",
                redirect: { name: 'products', query: { tab: 'categories' } },
            },
            {
                path: "/finance-accounting/product-list",
                name: "product-list",
                redirect: { name: 'products' },
            },
            {
                path: "/finance-accounting/booking-summary-report",
                name: "booking-summary-report",
                component: () => import("@/views/pages/back-office/booking-summary-list/BookingSummaryList.vue"),
                meta: {
                    pageTitle: "Booking Summary Report",
                    breadcrumbs: ["Finance", "Booking Summary Report"],
                    userFeature: null,
                },
            },
            // {
            //     path: "/back-office/bill-type-coa-list",
            //     name: "bill-type-coa-list",
            //     component: () => import("@/views/pages/back-office/bill-type-list/BillTypeList.vue"),
            //     meta: {
            //         pageTitle: "Bill Type List",
            //         breadcrumbs: ["Back Office", "Bill Type List"],
            //         userFeature: { featureName: 'bill_type_coa_list', metaName: 'can_view' },
            //     },
            // },

            {
                path: "/back-office/account-receivable/ar-aging-list",
                name: "ar-aging-list",
                component: () => import("@/views/pages/back-office/account-receivable/ar-aging-list/ARAgingList.vue"),
                meta: {
                    pageTitle: "Account Receivable Aging List",
                    breadcrumbs: ["Back Office", "Account Receivable", "AR Aging List"],
                    userFeature: { featureName: 'accounts_receivable_aging_list', metaName: 'can_view' },
                },
            },
            {
                path: "/back-office/account-receivable/ar-payment-list",
                name: "ar-payment-list",
                component: () => import("@/views/pages/back-office/account-receivable/ar-payment-list/ARPaymentList.vue"),
                meta: {
                    pageTitle: "Account Receivable Payment List",
                    breadcrumbs: ["Back Office", "Account Receivable", "AR Payment List"],
                    userFeature: { featureName: 'accounts_receivable_payment_list', metaName: 'can_view' },
                },
            },
            {
                path: "/back-office/account-receivable/ar-journal-manual",
                name: "ar-journal-manual",
                component: () => import("@/views/pages/test/Test.vue"),
                meta: {
                    pageTitle: "Account Receivable Journal Manual",
                    breadcrumbs: ["Back Office", "Account Receivable", "AR Journal Manual"],
                    userFeature: { featureName: 'accounts_receivable_journal_manual', metaName: 'can_view' },
                },
            },
            {
                path: "/back-office/account-receivable/ar-journal",
                name: "ar-journal",
                component: () => import("@/views/pages/test/Test.vue"),
                meta: {
                    pageTitle: "Account Receivable Journal",
                    breadcrumbs: ["Back Office", "Account Receivable", "AR Journal"],
                    userFeature: { featureName: 'accounts_receivable_journal', metaName: 'can_view' },
                },
            },

            {
                path: "/back-office/account-payable/supplier-list",
                name: "ap-supplier-list",
                redirect: { name: 'contacts', query: { type: 'supplier' } },
                meta: {
                    pageTitle: "Supplier List",
                    breadcrumbs: ["Back Office", "Account Payable", "Supplier List"],
                    userFeature: { featureName: 'account_payable_supplier_list', metaName: 'can_view' },
                },
            },
            {
                path: "/back-office/account-payable/expenses",
                redirect: { name: 'ap-expenses' },
            },
            {
                path: "/back-office/account-payable/ap-aging-list",
                name: "ap-aging-list",
                component: () => import("@/views/pages/test/Test.vue"),
                meta: {
                    pageTitle: "Account Payable Aging List",
                    breadcrumbs: ["Back Office", "Account Payable", "AP Aging List"],
                    userFeature: { featureName: 'account_payable_aging_list', metaName: 'can_view' },
                },
            },
            {
                path: "/back-office/account-payable/ap-payment-list",
                name: "ap-payment-list",
                component: () => import("@/views/pages/test/Test.vue"),
                meta: {
                    pageTitle: "Account Payable Payment List",
                    breadcrumbs: ["Back Office", "Account Payable", "AP Payment List"],
                    userFeature: { featureName: 'account_payable_payment_list', metaName: 'can_view' },
                },
            },
            {
                path: "/back-office/account-payable/ap-journal-manual",
                name: "ap-journal-manual",
                component: () => import("@/views/pages/test/Test.vue"),
                meta: {
                    pageTitle: "Account Payable Journal Manual",
                    breadcrumbs: ["Back Office", "Account Payable", "AP Journal Manual"],
                    userFeature: { featureName: 'account_payable_journal_manual', metaName: 'can_view' },
                },
            },
            {
                path: "/back-office/account-payable/ap-journal",
                name: "ap-journal",
                component: () => import("@/views/pages/test/Test.vue"),
                meta: {
                    pageTitle: "Account Payable Journal",
                    breadcrumbs: ["Back Office", "Account Payable", "AP Journal"],
                    userFeature: { featureName: 'account_payable_journal', metaName: 'can_view' },
                },
            },
            // Reports
            {
                path: "/reports/daily-revenue",
                name: "daily-revenue-report",
                component: () => import("@/views/pages/report/daily-revenue-report/DailyRevenueReport.vue"),
                meta: {
                    pageTitle: "Daily Revenue",
                    breadcrumbs: ["Reports", "Daily Revenue"],
                    userFeature: { featureName: 'daily_revenue_report', metaName: 'can_view' },
                },
            },
            // Reports Property Booking Pickup
            {
                path: "/reports/property-booking-pickup-report",
                name: "property-booking-pickup-report",
                component: () => import("@/views/pages/report/property-booking-pickup-report/BookingPickupReport.vue"),
                meta: {
                    pageTitle: "Booking Pickup Report",
                    breadcrumbs: ["Reports", "Finance", "Booking Pickup Report"],
                    userFeature: { featureName: 'property_booking_pickup_report', metaName: 'can_view' },
                },
            },
            ...REPORT_REGISTRY.map(entry => ({
                path: `/reports/${entry.reportKey}`,
                name: `report-${entry.reportKey}`,
                component: () => import('@/views/pages/report/reports-center/ReportsCenter.vue'),
                meta: {
                    pageTitle: entry.label,
                    breadcrumbs: ['Reports', entry.groupLabel, entry.label],
                    userFeature: {
                        featureName: entry.featureKey,
                        metaName: 'can_view',
                        fallbackFeatureName: entry.featureKeys?.find((k) => k !== entry.featureKey),
                    },
                    reportKey: entry.reportKey,
                },
            })),
            // Legacy Yearly / Date Range URLs → unified report + period mode
            {
                path: '/reports/occupancy-daily',
                redirect: { name: 'report-occupancy', query: { mode: 'date_range' } },
            },
            {
                path: '/reports/occupancy-monthly',
                redirect: { name: 'report-occupancy', query: { mode: 'yearly' } },
            },
            {
                path: '/reports/revenue-daily',
                redirect: { name: 'report-revenue', query: { mode: 'date_range' } },
            },
            {
                path: '/reports/revenue-monthly',
                redirect: { name: 'report-revenue', query: { mode: 'yearly' } },
            },
            {
                path: '/reports/agent-daily',
                redirect: { name: 'report-agent', query: { mode: 'date_range' } },
            },
            {
                path: '/reports/agent-monthly',
                redirect: { name: 'report-agent', query: { mode: 'yearly' } },
            },
            // Channel Managers -> OTA & Distributions
            {
                path: "/ota-distributions/property-settings",
                name: "cm-property-settings",
                component: () => import("@/views/pages/channel-manager/property-setting/PropertySetting.vue"),
                meta: {
                    pageTitle: "Property Settings",
                    breadcrumbs: ["OTA and Distributions", "Property Settings"],
                    userFeature: { featureName: 'property_settings', metaName: 'can_view' },
                },
            },
            {
                path: "/ota-distributions/country-mappings",
                name: "cm-country-mappings",
                component: () => import("@/views/pages/channel-manager/country-mapping/CountryMapping.vue"),
                meta: {
                    pageTitle: "Country Mappings",
                    breadcrumbs: ["OTA and Distributions", "Country Mappings"],
                    userFeature: { featureName: 'country_mappings', metaName: 'can_view' },
                },
            },
            {
                path: "/ota-distributions/room-type-mappings",
                name: "cm-room-type-mappings",
                component: () => import("@/views/pages/channel-manager/room-type-mapping/RoomTypeMapping.vue"),
                meta: {
                    pageTitle: "Room Type Mappings",
                    breadcrumbs: ["OTA and Distributions", "Mappings", "Room Type Mappings"],
                    userFeature: { featureName: 'smint_room_type_mappings', metaName: 'can_view' },
                },
            },
            {
                path: "/ota-distributions/rate-plan-mappings",
                name: "cm-rate-plan-mappings",
                component: () => import("@/views/pages/channel-manager/rate-plan-mapping/RatePlanMapping.vue"),
                meta: {
                    pageTitle: "Rate Plan Mappings",
                    breadcrumbs: ["OTA and Distributions", "Mappings", "Rate Plan Mappings"],
                    userFeature: { featureName: 'smint_rate_plans_mappings', metaName: 'can_view' },
                },
            },
            {
                path: "/ota-distributions/room-rate-mappings",
                name: "cm-roomtype-rateplan-mappings",
                component: () => import("@/views/pages/channel-manager/roomtype-rateplan-mapping/RoomtypeRateplanMapping.vue"),
                meta: {
                    pageTitle: "Room and Rate Mappings",
                    breadcrumbs: ["OTA and Distributions", "Room and Rate Mappings"],
                    userFeature: { featureName: 'room_rate_mappings', metaName: 'can_view' },
                },
            },
            {
                path: "/ota-distributions/agent-mappings",
                name: "cm-property-agent-mappings",
                component: () => import("@/views/pages/channel-manager/property-agent-mapping/PropertyAgentMapping.vue"),
                meta: {
                    pageTitle: "Agent Mappings",
                    breadcrumbs: ["OTA and Distributions", "Agent Mappings"],
                    userFeature: { featureName: 'agent_mappings', metaName: 'can_view' },
                },
            },
            {
                path: "/ota-distributions/bookings/booking-not-ack-channex",
                name: "cm-booking-not-ack-channex",
                component: () => import("@/views/pages/channel-manager/booking-receive/booking-not-ack-channex/BookingNotAck.vue"),
                meta: {
                    pageTitle: "Booking not Acknowledge (Channex)",
                    breadcrumbs: ["OTA and Distributions", "Bookings", "Booking not Acknowledge (Channex)"],
                    userFeature: { featureName: 'smint_booking_channex_not_acknowledge', metaName: 'can_view' },
                },
            },
            {
                path: "/ota-distributions/bookings/booking-receive-bookandlink",
                name: "cm-booking-receive-bookandlink",
                component: () => import("@/views/pages/channel-manager/booking-receive/booking-receive-bookandlink/BookingReceive.vue"),
                meta: {
                    pageTitle: "Booking Receive (BookandLink)",
                    breadcrumbs: ["OTA and Distributions", "Bookings", "Booking Receive (BookandLink)"],
                    userFeature: { featureName: 'smint_booking_book_and_link_receive', metaName: 'can_view' },
                },
            },
            {
                path: "/ota-distributions/availability-updates",
                component: { render: () => h(RouterView) },
                children: [
                    {
                        path: '',
                        name: "cm-availability-updates",
                        component: () => import("@/views/pages/channel-manager/availability-updates/AvailabilityUpdates.vue"),
                        meta: {
                            pageTitle: "Availability Updates",
                            breadcrumbs: ["OTA and Distributions", "Availability Updates"],
                            userFeature: { featureName: 'availability_updates', metaName: 'can_view' },
                        },
                    },
                    {
                        path: 'multiple',
                        name: "cm-availability-updates-multi",
                        component: () => import("@/views/pages/channel-manager/availability-updates/multiple-availability-updates/MultipleAvailabilityUpdates.vue"),
                        meta: {
                            pageTitle: "Multiple Availability Updates",
                            breadcrumbs: ["OTA and Distributions", "Availability Updates", "Multiple Availability Updates"],
                            userFeature: { featureName: 'availability_updates', metaName: 'can_view' },
                        },
                    },
                ],
            },
            {
                path: "/ota-distributions/price-and-restrictions",
                component: { render: () => h(RouterView) },
                children: [
                    {
                        path: '',
                        name: "cm-price-and-restriction-updates",
                        component: () => import("@/views/pages/channel-manager/price-and-restriction-updates/PriceAndRestrictionUpdates.vue"),
                        meta: {
                            pageTitle: "Price and Restrictions",
                            breadcrumbs: ["OTA and Distributions", "Price and Restrictions"],
                            userFeature: { featureName: 'price_restriction', metaName: 'can_view' },
                        },
                    },
                ],
            },
            {
                path: "/ota-distributions/dynamic-pricing",
                name: "cm-dynamic-pricing",
                component: () => import("@/views/pages/channel-manager/dynamic-pricing/DynamicPricing.vue"),
                meta: {
                    pageTitle: "Dynamic Pricing",
                    breadcrumbs: ["OTA and Distributions", "Dynamic Pricing"],
                    userFeature: { featureName: 'price_restriction', metaName: 'can_view' },
                },
            },
            {
                path: "/ota-distributions/sync-error-log",
                name: "cm-sync-error-log",
                component: () => import("@/views/pages/channel-manager/sync-error-log/SyncErrorLog.vue"),
                meta: {
                    pageTitle: "Sync Error Log",
                    breadcrumbs: ["OTA and Distributions", "Sync Error Log"],
                    userFeature: { featureName: 'cm_booking_sync_error', metaName: 'can_list' },
                },
            },
            {
                path: "/ota-distributions/activity-log",
                name: "cm-activity-log",
                component: () => import("@/views/pages/channel-manager/activity-log/ActivityLogCM.vue"),
                meta: {
                    pageTitle: "Activity Log",
                    breadcrumbs: ["OTA and Distributions", "Activity Log"],
                    userFeature: { featureName: 'activity_log', metaName: 'can_view' },
                },
            },
            {
                path: "/channel-managers/activity-log/channels/channex",
                name: "cm-activity-log-channex",
                component: () => import("@/views/pages/channel-manager/activity-log/channels/channex/ChannexLog.vue"),
                meta: {
                    pageTitle: "Activity Log - Channex",
                    breadcrumbs: ["Channel Managers", "Activity Log", "Channex Log"],
                    userFeature: { featureName: 'smint_channex_activity_log', metaName: 'can_view' },
                },
            },
            {
                path: "/channel-managers/activity-log/channels/bookandlink",
                name: "cm-activity-log-bookandlink",
                component: () => import("@/views/pages/channel-manager/activity-log/channels/bookandlink/BookandlinkLog.vue"),
                meta: {
                    pageTitle: "Activity Log - BookandLink",
                    breadcrumbs: ["Channel Managers", "Activity Log", "BookandLink Log"],
                    userFeature: { featureName: 'smint_book_and_link_activity_log', metaName: 'can_view' },
                },
            },
            // Property Setup -> Hotel Configuration
            {
                path: "/hotel-configuration/room-status",
                name: "room-status-list",
                component: () => import("@/views/pages/property-setup/room-status-list/RoomStatusList.vue"),
                meta: {
                    pageTitle: "Room Status",
                    breadcrumbs: ["Hotel Configuration", "Room Status"],
                    userFeature: { featureName: 'hotel_configuration_room_status', metaName: 'can_view' },
                },
            },
            {
                path: "/hotel-configuration/room-bed-type-list",
                name: "room-bed-type-list",
                component: () => import("@/views/pages/property-setup/room-bed-type-list/RoomBedTypeList.vue"),
                meta: {
                    pageTitle: "Bed Setup",
                    breadcrumbs: ["Hotel Configuration", "Bed Setup"],
                    userFeature: { featureName: 'bed_setup', metaName: 'can_view' },
                },
            },
            {
                path: '/hotel-configuration/room-types',
                component: { render: () => h(RouterView) },
                children: [
                    {
                        path: '',
                        name: "room-types",
                        component: () => import("@/views/pages/property-setup/room-types/RoomTypes.vue"),
                        meta: {
                            pageTitle: "Room Types",
                            breadcrumbs: ["Hotel Configuration", "Room Types"],
                            userFeature: { featureName: 'room_types', metaName: 'can_view' },
                        },
                    },
                    {
                        path: 'add',
                        name: 'room-types-add',
                        component: () => import("@/views/pages/property-setup/room-types/Add.vue"),
                        meta: {
                            pageTitle: "Add New Room Type",
                            breadcrumbs: ["Hotel Configuration", "Room Types", "Add New Room Type"],
                            userFeature: { featureName: 'room_types', metaName: 'can_add' },
                        },
                    },
                    {
                        path: 'edit/:id',
                        name: "room-types-edit",
                        component: () => import("@/views/pages/property-setup/room-types/Edit.vue"),
                        meta: {
                            pageTitle: "Edit Room Type",
                            breadcrumbs: ["Hotel Configuration", "Room Types", "Edit Room Type"],
                            userFeature: { featureName: 'room_types', metaName: 'can_edit' },
                        },
                    },
                    {
                        path: 'room-list/:id',
                        name: "room-types-room-list",
                        component: () => import("@/views/pages/property-setup/room-types/room-list/RoomList.vue"),
                        meta: {
                            pageTitle: "Room List",
                            breadcrumbs: ["Hotel Configuration", "Room Types", "Edit Room Type", "Room List"],
                            userFeature: { featureName: 'rooms_list', metaName: 'can_view' },
                        },
                    },
                ]
            },
            {
                path: '/hotel-configuration/rate-plans',
                component: { render: () => h(RouterView) },
                children: [
                    {
                        path: '',
                        name: "rate-plans",
                        component: () => import("@/views/pages/property-setup/room-rates/RoomRates.vue"),
                        meta: {
                            pageTitle: "Rate Plans",
                            breadcrumbs: ["Hotel Configuration", "Rate Plans"],
                            userFeature: { featureName: 'rate_plans', metaName: 'can_view' },
                        },
                    },
                    {
                        path: 'add',
                        name: "rate-plans-add",
                        component: () => import("@/views/pages/property-setup/room-rates/Add.vue"),
                        meta: {
                            pageTitle: "Add Rate Plan",
                            breadcrumbs: ["Hotel Configuration", "Rate Plans", "Add Rate Plan"],
                            userFeature: { featureName: 'rate_plans', metaName: 'can_add' },
                        },
                    },
                    {
                        path: 'edit/:id',
                        name: "rate-plans-edit",
                        component: () => import("@/views/pages/property-setup/room-rates/Edit.vue"),
                        meta: {
                            pageTitle: "Edit Rate Plan",
                            breadcrumbs: ["Hotel Configuration", "Rate Plans", "Edit Rate Plan"],
                            userFeature: { featureName: 'rate_plans', metaName: 'can_edit' },
                        },
                    },
                ],
            },
            {
                path: "/hotel-configuration/departments",
                name: "departments",
                component: () => import("@/views/pages/property-setup/departments/Depatments.vue"),
                meta: {
                    pageTitle: "Departments",
                    breadcrumbs: ["Hotel Configuration", "Departments"],
                    userFeature: { featureName: 'departments', metaName: 'can_view' },
                },
            },
            {
                path: "/hotel-configuration/guest-policy",
                name: "guest-policy",
                component: () => import("@/views/pages/property-setup/guest-policy/GuestPolicy.vue"),
                meta: {
                    pageTitle: "Guest Policy",
                    breadcrumbs: ["Hotel Configuration", "Guest Policy"],
                    userFeature: { featureName: 'guest_policy', metaName: 'can_view' },
                },
            },
            {
                path: "/hotel-configuration/product-category",
                redirect: { name: 'products', query: { tab: 'categories' } },
            },
            {
                path: "/hotel-configuration/product-list",
                redirect: { name: 'products' },
            },
            {
                path: "/hotel-configuration/coa-setup-hub",
                name: "coa-setup-hub",
                component: () => import("@/views/pages/property-setup/coa-setup-hub/CoaSetupHub.vue"),
                meta: {
                    pageTitle: "Chart of Account Setup",
                    breadcrumbs: ["Hotel Configuration", "Chart of Account Setup"],
                    userFeature: { featureName: 'chart_of_account_setup', metaName: 'can_list' },
                },
            },
            {
                path: "/hotel-configuration/chartofaccount-setup",
                name: "default-chartofaccount-mapping",
                redirect: { name: 'coa-setup-hub', query: { tab: 'payments' } },
            },
            {
                path: "/hotel-configuration/chartofaccount-setup-legacy",
                name: "default-chartofaccount-mapping-legacy",
                component: () => import("@/views/pages/property-setup/default-coa-mapping/DefaultCoAMapping.vue"),
                meta: {
                    pageTitle: "Chart of Account Setup",
                    breadcrumbs: ["Hotel Configuration", "Chart of Account Setup"],
                    userFeature: { featureName: 'chart_of_account_setup', metaName: 'can_list' },
                },
            },
            {
                path: "/hotel-configuration/import-future-bookings",
                name: "future-booking-import",
                component: () => import("@/views/pages/property-setup/future-booking-import/FutureBookingImport.vue"),
                meta: {
                    pageTitle: "Import Future Bookings",
                    breadcrumbs: ["Hotel Configuration", "Import Future Bookings"],
                    userFeature: { featureName: 'import_future_bookings', metaName: 'can_list' },
                },
            },
            // System Setup
            {
                path: "/system-setup/feature-category",
                name: "feature-category",
                component: () => import("@/views/pages/system-setup/feature-category/FeatureCategory.vue"),
                meta: {
                    pageTitle: "Feature Category",
                    breadcrumbs: ["System Setup", "Features", "Category"],
                    userFeature: { featureName: 'features_categories', metaName: 'can_view' },
                },
            },
            {
                path: "/system-setup/feature",
                name: "feature",
                component: () => import("@/views/pages/system-setup/feature/Feature.vue"),
                meta: {
                    pageTitle: "Feature List",
                    breadcrumbs: ["System Setup", "Features", "Features"],
                    userFeature: { featureName: 'features', metaName: 'can_view' },
                },
            },
            {
                path: "/system-setup/hotel-group",
                name: "property-group",
                component: () => import("@/views/pages/system-setup/property-group/PropertyGroup.vue"),
                meta: {
                    pageTitle: "Hotel Group",
                    breadcrumbs: ["System Setup", "Properties", "Hotel Group"],
                    userFeature: { featureName: 'hotel_group', metaName: 'can_view' },
                },
            },
            {
                path: "/system-setup/hotels",
                name: "property",
                component: () => import("@/views/pages/system-setup/property/Property.vue"),
                meta: {
                    pageTitle: "Hotels",
                    breadcrumbs: ["System Setup", "Properties", "Hotels"],
                    userFeature: { featureName: 'hotels', metaName: 'can_view' },
                },
            },
            {
                path: "/system-setup/hotels/:id/onboarding",
                name: "property-onboarding",
                component: () => import("@/views/pages/system-setup/property/PropertyOnboarding.vue"),
                meta: {
                    pageTitle: "Hotel Setup Workspace",
                    breadcrumbs: ["System Setup", "Properties", "Hotels", "Setup"],
                    userFeature: { featureName: 'hotels', metaName: 'can_view' },
                },
            },
            {
                path: "/system-setup/default-user-feature",
                name: "user-feature-category-default",
                component: () => import("@/views/pages/system-setup/user-category-feature-default/UserCategoryFeatureDefault.vue"),
                meta: {
                    pageTitle: "Default User Feature",
                    breadcrumbs: ["System Setup", "Features", "Default User Feature"],
                    userFeature: { featureName: 'default_user_features', metaName: 'can_view' },
                },
            },
            {
                path: "/system-setup/user-activity-log",
                name: "user-activity-log",
                component: () => import("@/views/pages/system-setup/user-activity-log/UserActivityLog.vue"),
                meta: {
                    pageTitle: "User Activity Log",
                    breadcrumbs: ["System Setup", "User Activity Log"],
                    userFeature: { featureName: 'user_activity_log', metaName: 'can_view' },
                },
            },
            {
                path: "/system-setup/security-dashboard",
                name: "security-dashboard",
                component: () => import("@/views/pages/system-setup/security-dashboard/SecurityDashboard.vue"),
                meta: {
                    pageTitle: "Security Dashboard",
                    breadcrumbs: ["System Setup", "Security Dashboard"],
                    userFeature: { featureName: 'user_activity_log', metaName: 'can_view' },
                },
            },
            {
                path: '/system-setup/users',
                component: { render: () => h(RouterView) },
                children: [
                    {
                        path: '',
                        name: 'users',
                        component: () => import("@/views/pages/system-setup/user/User.vue"),
                        meta: {
                            pageTitle: "Users",
                            breadcrumbs: ["System Setup", "Users"],
                            userFeature: { featureName: 'users_settings', metaName: 'can_view' },
                        },
                    },
                    {
                        path: 'add',
                        name: 'users-add',
                        component: () => import("@/views/pages/system-setup/user/Add.vue"),
                        meta: {
                            pageTitle: "Add New User",
                            breadcrumbs: ["System Setup", "Users", "Add New User"],
                            userFeature: { featureName: 'users_settings', metaName: 'can_add' },
                        },
                    },
                    {
                        path: 'edit/:id',
                        name: "users-edit",
                        component: () => import("@/views/pages/system-setup/user/Edit.vue"),
                        meta: {
                            pageTitle: "Edit User",
                            breadcrumbs: ["System Setup", "Users", "Edit User"],
                            userFeature: { featureName: 'users_settings', metaName: 'can_edit' },
                        },
                    }
                ]
            },
            {
                path: "/system-setup/room-status",
                name: "room-status-default",
                component: () => import("@/views/pages/system-setup/room-status-default/RoomStatusDefault.vue"),
                meta: {
                    pageTitle: "Room Status",
                    breadcrumbs: ["System Setup", "Default Hotel Setup", "Room Status"],
                    userFeature: { featureName: 'system_setup_room_status', metaName: 'can_view' },
                },
            },
            {
                path: "/system-setup/default-amenities",
                name: "amenities-list-default",
                component: () => import("@/views/pages/system-setup/amenities-list-default/AmenitiesListDefault.vue"),
                meta: {
                    pageTitle: "Amenities",
                    breadcrumbs: ["System Setup", "Default Hotel Setup", "Amenities"],
                    userFeature: { featureName: 'amenities', metaName: 'can_view' },
                },
            },
            {
                path: "/system-setup/chart-of-accounts",
                name: "chart-of-account-default",
                component: () => import("@/views/pages/system-setup/chart-of-account-default/ChartofAccountDefault.vue"),
                meta: {
                    pageTitle: "Chart of Accounts",
                    breadcrumbs: ["System Setup", "Default Hotel Setup", "Chart of Accounts"],
                    userFeature: { featureName: 'chart_of_accounts', metaName: 'can_view' },
                },
            },
            {
                path: "/system-setup/coa-template",
                component: { render: () => h(RouterView) },
                children: [
                    {
                        path: '',
                        name: "chart-of-account-template",
                        component: () => import("@/views/pages/system-setup/chart-of-account-template/ChartofAccountTemplate.vue"),
                        meta: {
                            pageTitle: "Chart of Account Template",
                            breadcrumbs: ["System Setup", "Default Hotel Setup", "Chart of Account Template"],
                            userFeature: { featureName: 'coa_template', metaName: 'can_view' },
                        },
                    },
                    {
                        path: 'list/:id',
                        name: "chart-of-account-template-list",
                        component: () => import("@/views/pages/system-setup/chart-of-account-template/ChartofAcountList.vue"),
                        meta: {
                            pageTitle: "List Chart of Account Template",
                            breadcrumbs: ["System Setup", "Default Hotel Setup", "Chart of Account Template", "List Chart of Account Template"],
                            userFeature: { featureName: 'coa_template', metaName: 'can_list' },
                        },
                    },

                ],
            },
            // {
            //     path: "/system-setup/bill-type-default",
            //     name: "bill-type-default",
            //     component: () => import("@/views/pages/system-setup/bill-type/BillType.vue"),
            //     meta: {
            //         pageTitle: "Bill Type Default",
            //         breadcrumbs: ["System Setup", "Default Hotel Setup", "Bill Type Default"],
            //         userFeature: { featureName: 'bill_type_default', metaName: 'can_view' },
            //     },
            // },
            {
                path: "/system-setup/payment-collection-types",
                name: "payment-collect-type",
                component: () => import("@/views/pages/system-setup/payment-collect-type/PaymentCollectType.vue"),
                meta: {
                    pageTitle: "Payment Collection Types",
                    breadcrumbs: ["System Setup", "Default Hotel Setup", "Payment Collection Types"],
                    userFeature: { featureName: 'payment_collection_types', metaName: 'can_view' },
                },
            },
            {
                path: "/system-setup/guest-policy-template",
                name: "guest-policy-template",
                component: () => import("@/views/pages/system-setup/guest-policy-template/GuestPolicyTemplate.vue"),
                meta: {
                    pageTitle: "Guest Policy Template",
                    breadcrumbs: ["System Setup", "Guest Policy Template"],
                    userFeature: { featureName: 'guest_policy_template', metaName: 'can_view' },
                },
            },
            // General Settings
            {
                path: "/general-settings/countries",
                name: "country-settings",
                component: () => import("@/views/pages/general-setting/country/Country.vue"),
                meta: {
                    pageTitle: "Countries",
                    breadcrumbs: ["General Settings", "Countries"],
                    userFeature: { featureName: 'country_settings', metaName: 'can_view' },
                },
            },
            {
                path: "/general-settings/regions",
                name: "region-settings",
                component: () => import("@/views/pages/general-setting/region/Region.vue"),
                meta: {
                    pageTitle: "Regions",
                    breadcrumbs: ["General Settings", "Regions"],
                    userFeature: { featureName: 'region_settings', metaName: 'can_view' },
                },
            },
            {
                path: "/general-settings/areas",
                name: "area-settings",
                component: () => import("@/views/pages/general-setting/area/Area.vue"),
                meta: {
                    pageTitle: "Areas",
                    breadcrumbs: ["General Settings", "Areas"],
                    userFeature: { featureName: 'area_settings', metaName: 'can_view' },
                },
            },
            {
                path: "/general-settings/languages",
                name: "language-settings",
                component: () => import("@/views/pages/general-setting/language/Language.vue"),
                meta: {
                    pageTitle: "Languages",
                    breadcrumbs: ["General Settings", "Languages"],
                    userFeature: { featureName: 'language_settings', metaName: 'can_view' },
                },
            },
            {
                path: "/general-settings/currencies",
                name: "currency-settings",
                component: () => import("@/views/pages/general-setting/currency/Currency.vue"),
                meta: {
                    pageTitle: "Currencies",
                    breadcrumbs: ["General Settings", "Currencies"],
                    userFeature: { featureName: 'currency_settings', metaName: 'can_view' },
                },
            },
            {
                path: "/general-settings/timezones",
                name: "timezone-settings",
                component: () => import("@/views/pages/general-setting/timezone/Timezone.vue"),
                meta: {
                    pageTitle: "Timezones",
                    breadcrumbs: ["General Settings", "Timezones"],
                    userFeature: { featureName: 'timezone_settings', metaName: 'can_view' },
                },
            },
            {
                path: "/download-list",
                name: "download-list",
                component: () => import("@/views/pages/download-list/DownloadList.vue"),
                meta: {
                    pageTitle: "Download List",
                    breadcrumbs: ["Download List"],
                    userFeature: null,
                },
            },
            {
                path: "/account-overview",
                name: "account-overview",
                component: () => import("@/views/pages/account/Account.vue"),
                meta: {
                    pageTitle: "Account Overview",
                    breadcrumbs: ["Account", "Overview"],
                    userFeature: null,
                },
            },
            {
                path: "/account-update",
                name: "account-update",
                component: () => import("@/views/pages/account/Edit.vue"),
                meta: {
                    pageTitle: "Account Update",
                    breadcrumbs: ["Account", "Update"],
                    userFeature: null,
                },
            },
        ],
    },
    // routes using auth layout
    {
        path: "/",
        component: () => import("@/layouts/AuthLayout.vue"),
        meta: {
            middleware: "public",
        },
        children: [
            {
                path: "/sign-in",
                name: "sign-in",
                component: () =>
                    import("@/views/pages/authentication/SignIn.vue"),
                meta: {
                    pageTitle: "Sign In",
                    userFeature: null,
                },
            },
            {
                path: "/sign-up",
                name: "sign-up",
                component: () =>
                    import("@/views/pages/authentication/SignUp.vue"),
                meta: {
                    pageTitle: "Sign Up",
                    userFeature: null,
                },
            },
            {
                path: "/password-reset",
                name: "password-reset",
                component: () =>
                    import("@/views/pages/authentication/PasswordReset.vue"),
                meta: {
                    pageTitle: "Password reset",
                    userFeature: null,
                },
            },
            {
                path: "/password-reset/confirm",
                name: "password-reset-confirm",
                component: () =>
                    import("@/views/pages/authentication/PasswordResetConfirm.vue"),
                meta: {
                    pageTitle: "Set new password",
                    userFeature: null,
                },
            },
            {
                path: "/must-change-password",
                name: "must-change-password",
                component: () =>
                    import("@/views/pages/authentication/MustChangePassword.vue"),
                meta: {
                    pageTitle: "Change password required",
                    userFeature: null,
                },
            },
            {
                path: "/security/not-me",
                name: "security-not-me",
                component: () =>
                    import("@/views/pages/authentication/NotMeResult.vue"),
                meta: {
                    pageTitle: "Account security",
                    userFeature: null,
                },
            },
        ],
    },
    // only multi step sign up
    {
        path: "/multi-step-sign-up",
        name: "multi-step-sign-up",
        component: () =>
            import("@/views/pages/authentication/MultiStepSignUp.vue"),
        meta: {
            pageTitle: "Multi-step Sign up",
            userFeature: null,
        },
    },
    // routes using system layout
    {
        path: "/",
        component: () => import("@/layouts/SystemLayout.vue"),
        meta: {
            middleware: "public",
        },
        children: [
            {
                // the 404 route, when none of the above matches
                path: "/404",
                name: "404",
                component: () => import("@/views/pages/basic-flow/Error404.vue"),
                meta: {
                    pageTitle: "Error 404",
                    userFeature: null,
                },
            },
            {
                path: "/500",
                name: "500",
                component: () => import("@/views/pages/basic-flow/Error500.vue"),
                meta: {
                    pageTitle: "Error 500",
                    userFeature: null,
                },
            },
            {
                path: "/booking/voucher/:token",
                name: "file-booking-voucher",
                component: () => import("@/views/pages/front-office/booking/templates/BookingVoucher.vue"),
                meta: {
                    pageTitle: "Booking Voucher",
                    userFeature: null,
                },
            },
            {
                path: "/booking/billing/:model/:token",
                name: "file-booking-billing",
                component: () => import("@/views/pages/front-office/booking/templates/PrintBilling.vue"),
                meta: {
                    middleware: "auth",
                    pageTitle: "Print Billing",
                    userFeature: null,
                },
            },
            {
                path: "/booking/proforma-invoice/:model/:token",
                name: "file-booking-proforma-invoice",
                component: () => import("@/views/pages/front-office/booking/templates/PrintProformaInvoice.vue"),
                meta: {
                    middleware: "auth",
                    pageTitle: "Print Proforma Invoice",
                    userFeature: null,
                },
            },
            {
                path: "/booking/registration-form/:model/:token",
                name: "file-booking-registration-form",
                component: () => import("@/views/pages/front-office/booking/templates/PrintRegForm.vue"),
                meta: {
                    middleware: "auth",
                    pageTitle: "Print Registration Form",
                    userFeature: null,
                },
            },
            {
                path: "/registration/:token",
                name: "guest-registration-mobile",
                component: () => import("@/views/pages/front-office/booking/templates/MobileRegForm.vue"),
                meta: {
                    pageTitle: "Guest Registration",
                    userFeature: null,
                },
            },
        ],
    },
    // handle redirection missmatch route
    {
        path: "/:pathMatch(.*)*",
        redirect: "/404",
    },
];

const router = createRouter({
    history: createWebHistory(),
    routes,
});

router.beforeEach((to, from, next) => {
    // current page view title
    document.title = `${to.meta.pageTitle} - ${import.meta.env.VITE_NAME}`;

    // reset config to initial state
    store.commit(Mutations.RESET_LAYOUT_CONFIG);

    // // Cancel all requests from the previous page
    // ApiService.abortController.abort();
    // // Re-initialize for the next page's requests
    // ApiService.resetAbortController();

    switch (to.meta.middleware) {
        case "auth":
            Promise.resolve(
                // verify auth token before each page change
                store.dispatch(Actions.VERIFY_AUTH, {})
            ).finally(() => {
                // before page access check if page requires authentication
                ApiService.setHeader();
                if (store.getters.isUserAuthenticated) {
                    next();
                } else {
                    next({ name: "sign-in", query: { reason: "session" } });
                }
            });
            break;

        case "public":
            ApiService.setFreeHeader();
            next();
            break;

        default:
            ApiService.setFreeHeader();
            next();
            break;
    }

    // if (to.meta.middleware === "public") {
    //     ApiService.setFreeHeader();
    //     next();
    // } else {
    //     Promise.resolve(
    //         // verify auth token before each page change
    //         store.dispatch(Actions.VERIFY_AUTH, { token: JwtService.getToken() })
    //     ).finally(() => {
    //         // before page access check if page requires authentication
    //         if (to.meta.middleware === "auth") {
    //             if (store.getters.isUserAuthenticated) {
    //                 next();
    //             } else {
    //                 next({ name: "sign-in" });
    //             }
    //         } else {
    //             next();
    //         }
    //     });
    // }

    // Scroll page to top on every route change
    window.scrollTo({
        top: 0,
        left: 0,
        behavior: "smooth",
    });
});

const userFeaturesReady = (): boolean => {
    const features = store.getters.getUserFeatures;
    return !!(features && typeof features === "object" && Object.keys(features).length > 0);
};

const resolveFeatureAccess = (
    to: typeof router.currentRoute.value,
    next: (arg?: any) => void
) => {
    if (to.name === "coa-setup-hub") {
        const canAccess = AuthService.isUserFeature("chart_of_account_setup", "can_list")
            || AuthService.isUserFeature("chart_of_account_setup", "can_edit");
        if (canAccess) {
            next();
        } else {
            next({ name: "404" });
        }
        return;
    }
    if (to.meta.userFeature) {
        const userFeature = to.meta.userFeature as { featureName: string; metaName: string; fallbackFeatureName?: string };
        const hasFeature = AuthService.isUserFeature(userFeature.featureName, userFeature.metaName)
            || (!!userFeature.fallbackFeatureName && AuthService.isUserFeature(userFeature.fallbackFeatureName, userFeature.metaName));
        if (hasFeature) {
            next();
        } else {
            next({ name: "404" });
        }
        return;
    }
    next();
};

router.beforeResolve((to, from, next) => {
    if (to.name) {
        tricklingProgress.start();
    }
    const needsFeatureGate = to.name === "coa-setup-hub" || !!to.meta.userFeature;
    // After VERIFY_AUTH, features may still be empty on a rare race — re-verify once before 404.
    if (needsFeatureGate && !userFeaturesReady() && store.getters.isUserAuthenticated && JwtService.hasAuth()) {
        store.dispatch(Actions.VERIFY_AUTH, {}).finally(() => {
            resolveFeatureAccess(to, next);
        });
        return;
    }
    resolveFeatureAccess(to, next);
});

router.afterEach((to, from) => {
    tricklingProgress.done();
});

// After a new build is deployed, a tab still running the old bundle asks for page chunks that no
// longer exist, so menu clicks silently do nothing. Load the target page fresh instead (once per URL).
const CHUNK_RELOAD_KEY = "chunk-reload-target";
const isChunkLoadError = (error: any) =>
    /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module|Unable to preload CSS/i
        .test(String(error?.message || error || ""));

const reloadForNewBuild = (targetPath: string) => {
    try {
        if (sessionStorage.getItem(CHUNK_RELOAD_KEY) === targetPath) return false;
        sessionStorage.setItem(CHUNK_RELOAD_KEY, targetPath);
    } catch { /* storage disabled: still reload once */ }
    window.location.assign(targetPath);
    return true;
};

router.onError((error, to) => {
    tricklingProgress.done();
    if (isChunkLoadError(error)) {
        reloadForNewBuild(to?.fullPath || window.location.pathname);
    }
});

router.isReady().then(() => {
    try { sessionStorage.removeItem(CHUNK_RELOAD_KEY); } catch { /* ignore */ }
});

declare module 'vue-router' {
    interface RouteMeta {
        reportKey?: string;
    }
}

export default router;
